import _ from 'lodash';
import { TwTheme } from '../../../Tw';
import { getBestContrastColor } from '../../common/color.helper';

export interface WaitTimeThreshold {
  id: string;
  label: string;
  description: string;
  minSeconds: number;
  maxSeconds: number;
  color: string;
  backgroundColor: string;
  classNameTd: string;
  timelineColors: [string, string];
  severity: 'none' | 'low' | 'medium' | 'high' | 'critical';
  actionRequired: string;
}

export interface WaitTimeClassificationResult {
  threshold: WaitTimeThreshold;
  waitTimeSeconds: number;
  isOverdue: boolean;
}

export class WaitTimeClassificationModel {
  private thresholds: WaitTimeThreshold[];

  constructor(
    warningThresholdSeconds: number = 15 * 60, // 15 minutes
    lateThresholdSeconds: number = 30 * 60, // 30 minutes
    veryLateThresholdSeconds: number = 60 * 60, // 60 minutes
    customThresholds?: WaitTimeThreshold[],
  ) {
    if (customThresholds && customThresholds.length > 0) {
      // Use custom thresholds if provided and valid
      this.thresholds = this.sortAndValidateThresholds(
        this.ensureValidColors(customThresholds),
      );
    } else {
      // Use default threshold structure with properly formatted colors
      const colors = TwTheme.extend.colors;

      this.thresholds = [
        {
          id: 'on-time',
          label: 'On Time',
          description: 'Completed before or on deadline',
          minSeconds: 0,
          maxSeconds: 0,
          color: colors.menu.text,
          backgroundColor: this.hexToRgba(colors.benchmark.target, 0.6),
          classNameTd: '!bg-benchmark-target/10',
          timelineColors: [colors.benchmark.target, colors.menu.text] as [
            string,
            string,
          ],
          severity: 'none',
          actionRequired: 'No issue',
        },
        {
          id: 'slightly-late',
          label: 'Slightly Late',
          description: 'Minor delay, within buffer',
          minSeconds: 0.01,
          maxSeconds: warningThresholdSeconds,
          color: colors.menu.text,
          backgroundColor: this.hexToRgba(colors.benchmark.warning, 0.3),
          classNameTd: '!bg-benchmark-warning/30',
          timelineColors: [colors.benchmark.warning, colors.menu.text] as [
            string,
            string,
          ],
          severity: 'low',
          actionRequired: 'Monitor casually',
        },
        {
          id: 'late',
          label: 'Late',
          description: 'Delay, could impact downstream tasks',
          minSeconds: warningThresholdSeconds,
          maxSeconds: lateThresholdSeconds,
          color: colors.menu.text,
          backgroundColor: this.hexToRgba(colors.alerts.warning, 0.4),
          classNameTd: '!bg-alerts-warning/40',
          timelineColors: [colors.alerts.warning, colors.menu.text] as [
            string,
            string,
          ],
          severity: 'medium',
          actionRequired: 'Review workload/bottlenecks',
        },
        {
          id: 'very-late',
          label: 'Very Late',
          description: 'Significant lateness',
          minSeconds: lateThresholdSeconds,
          maxSeconds: veryLateThresholdSeconds,
          color: colors.menu.text,
          backgroundColor: this.hexToRgba(colors.alerts.warning, 0.7),
          classNameTd: '!bg-alerts-warning/70',
          timelineColors: [colors.alerts.warning, colors.menu.text] as [
            string,
            string,
          ],
          severity: 'high',
          actionRequired: 'Escalate cause',
        },
        {
          id: 'critically-late',
          label: 'Critically Late',
          description: 'Extremely overdue, major risk',
          minSeconds: veryLateThresholdSeconds,
          maxSeconds: Infinity,
          color: colors.menu.text,
          backgroundColor: this.hexToRgba(colors.alerts.error, 0.5),
          classNameTd: '!bg-alerts-error/50',
          timelineColors: [colors.alerts.error, colors.menu.text] as [
            string,
            string,
          ],
          severity: 'critical',
          actionRequired: 'Immediate action required',
        },
      ];
    }
  }

  /**
   * Ensure all thresholds have valid colors
   */
  private ensureValidColors(
    thresholds: WaitTimeThreshold[],
  ): WaitTimeThreshold[] {
    const defaultColors = [
      '#FF9F5B',
      '#FF476E',
      '#6F8DE8',
      '#8DC452',
      '#EA5275',
      '#3EC0E8',
      '#FF8939',
      '#F9B428',
      '#5FA7E9',
      '#8CC464',
    ];

    return thresholds.map((threshold, index) => {
      let needsColorFix = false;
      let backgroundColor = threshold.backgroundColor;
      let color = threshold.color;

      // Check if colors are invalid or missing
      if (
        !backgroundColor ||
        backgroundColor === 'undefined' ||
        backgroundColor === ''
      ) {
        backgroundColor = defaultColors[index % defaultColors.length];
        needsColorFix = true;
      }

      if (!color || color === 'undefined' || color === '') {
        color = getBestContrastColor(backgroundColor);
        needsColorFix = true;
      }

      if (needsColorFix) {
        console.warn(`Fixed missing colors for threshold ${threshold.id}:`, {
          originalBg: threshold.backgroundColor,
          originalColor: threshold.color,
          newBg: backgroundColor,
          newColor: color,
        });
      }

      return {
        ...threshold,
        backgroundColor,
        color,
        timelineColors: [backgroundColor, color] as [string, string],
      };
    });
  }

  /**
   * Create a deep clone of a threshold object to avoid read-only property issues
   */
  public static deepCloneThreshold(
    threshold: WaitTimeThreshold,
  ): WaitTimeThreshold {
    return {
      id: threshold.id,
      label: threshold.label,
      description: threshold.description,
      minSeconds: threshold.minSeconds,
      maxSeconds: threshold.maxSeconds,
      color: threshold.color,
      backgroundColor: threshold.backgroundColor,
      classNameTd: threshold.classNameTd,
      timelineColors: [
        threshold.timelineColors[0],
        threshold.timelineColors[1],
      ] as [string, string],
      severity: threshold.severity,
      actionRequired: threshold.actionRequired,
    };
  }

  /**
   * Convert hex color to rgba with alpha
   */
  private hexToRgba(hex: string, alpha: number): string {
    // Handle both HSL and hex colors
    if (hex.startsWith('hsl')) {
      // For HSL colors, convert to rgba by extracting HSL values and adding alpha
      const match = hex.match(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/);
      if (match) {
        return `hsla(${match[1]}, ${match[2]}%, ${match[3]}%, ${alpha})`;
      }
      return hex; // fallback to original
    }

    // For hex colors
    const cleanHex = hex.replace('#', '');
    const r = parseInt(cleanHex.substring(0, 2), 16);
    const g = parseInt(cleanHex.substring(2, 4), 16);
    const b = parseInt(cleanHex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  /**
   * Sort and validate thresholds to ensure proper ranges
   */
  private sortAndValidateThresholds(
    thresholds: WaitTimeThreshold[],
  ): WaitTimeThreshold[] {
    const sorted = _.sortBy(thresholds, 'maxSeconds');

    // Ensure ranges don't overlap and are valid
    for (let i = 1; i < sorted.length; i++) {
      const current = sorted[i];
      const previous = sorted[i - 1];

      // Adjust minSeconds to match previous threshold's maxSeconds
      if (current.minSeconds < previous.maxSeconds) {
        current.minSeconds = previous.maxSeconds;
      }
    }

    return sorted;
  }

  /**
   * Set custom thresholds
   */
  setCustomThresholds(thresholds: WaitTimeThreshold[]): void {
    this.thresholds = this.sortAndValidateThresholds(thresholds);
  }

  /**
   * Add a new threshold
   */
  addThreshold(threshold: WaitTimeThreshold): void {
    this.thresholds.push(threshold);
    this.thresholds = this.sortAndValidateThresholds(this.thresholds);
  }

  /**
   * Remove a threshold by ID
   */
  removeThreshold(thresholdId: string): void {
    this.thresholds = this.thresholds.filter(t => t.id !== thresholdId);
    this.thresholds = this.sortAndValidateThresholds(this.thresholds);
  }

  /**
   * Update a specific threshold
   */
  updateThreshold(
    thresholdId: string,
    updates: Partial<WaitTimeThreshold>,
  ): void {
    const index = this.thresholds.findIndex(t => t.id === thresholdId);
    if (index !== -1) {
      this.thresholds[index] = { ...this.thresholds[index], ...updates };
      this.thresholds = this.sortAndValidateThresholds(this.thresholds);
    }
  }

  /**
   * Classify wait time and return the appropriate threshold
   */
  classify(waitTimeSeconds: number): WaitTimeClassificationResult {
    // Find the appropriate threshold
    const threshold = this.thresholds.find(
      t => waitTimeSeconds >= t.minSeconds && waitTimeSeconds <= t.maxSeconds,
    );

    return {
      threshold: threshold || this.thresholds[this.thresholds.length - 1], // fallback to most severe
      waitTimeSeconds,
      isOverdue: waitTimeSeconds > 0,
    };
  }

  /**
   * Get all thresholds for configuration UI
   */
  getAllThresholds(): WaitTimeThreshold[] {
    return [...this.thresholds];
  }

  /**
   * Update threshold values (for configuration)
   */
  updateThresholds(
    warningThresholdSeconds: number,
    lateThresholdSeconds: number,
    veryLateThresholdSeconds: number,
  ): void {
    // Update the threshold ranges
    this.thresholds[1].maxSeconds = warningThresholdSeconds;
    this.thresholds[2].minSeconds = warningThresholdSeconds;
    this.thresholds[2].maxSeconds = lateThresholdSeconds;
    this.thresholds[3].minSeconds = lateThresholdSeconds;
    this.thresholds[3].maxSeconds = veryLateThresholdSeconds;
    this.thresholds[4].minSeconds = veryLateThresholdSeconds;
  }

  /**
   * Get threshold configuration for UI display
   */
  getThresholdConfig(): {
    warningThresholdSeconds: number;
    lateThresholdSeconds: number;
    veryLateThresholdSeconds: number;
  } {
    return {
      warningThresholdSeconds: this.thresholds[1].maxSeconds,
      lateThresholdSeconds: this.thresholds[2].maxSeconds,
      veryLateThresholdSeconds: this.thresholds[3].maxSeconds,
    };
  }

  /**
   * Get summary statistics for a set of wait times
   */
  getStatistics(waitTimes: number[]): {
    total: number;
    byStatus: Record<string, number>;
    percentages: Record<string, number>;
  } {
    const total = waitTimes.length;
    const byStatus: Record<string, number> = {};

    // Initialize counts
    this.thresholds.forEach(t => {
      byStatus[t.id] = 0;
    });

    // Count by status
    waitTimes.forEach(waitTime => {
      const result = this.classify(waitTime);
      byStatus[result.threshold.id]++;
    });

    // Calculate percentages
    const percentages: Record<string, number> = {};
    Object.keys(byStatus).forEach(key => {
      percentages[key] = total > 0 ? (byStatus[key] / total) * 100 : 0;
    });

    return {
      total,
      byStatus,
      percentages,
    };
  }
}

// Default instance with sensible defaults
export const defaultWaitTimeClassification = new WaitTimeClassificationModel();
